// src/pages/trading-portal/AIAnalysisEngine/SentimentAlternativeDataPage.js
// Sentiment & Alternative Data - tabbed interface for news, social, events, options flow, macro

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Grid,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
    Skeleton,
    Card,
    CardContent,
    Tooltip,
    LinearProgress,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Collapse,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Newspaper as NewspaperIcon,
    Forum as ForumIcon,
    Event as EventIcon,
    Waves as WavesIcon,
    AccountBalance as AccountBalanceIcon,
    PlayArrow as PlayArrowIcon,
    OpenInNew as OpenInNewIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    CloudDownload as CloudDownloadIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Launch as LaunchIcon,
    Add as AddIcon,
    Assessment as AssessmentIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    SentimentScoreLabels,
    SentimentScoreColors,
    EventImpactLevelLabels,
    EventImpactLevelColors,
    FlowSignalTypeLabels,
    FlowSignalTypeColors,
    MacroIndicatorSignalLabels,
    MacroIndicatorSignalColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const SentimentAlternativeDataPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);

    // Tab
    const [activeTab, setActiveTab] = useState(0);

    // Global state
    const [error, setError] = useState(null);

    // ========== Tab 0: News Sentiment ==========
    const [newsItems, setNewsItems] = useState([]);
    const [newsLoading, setNewsLoading] = useState(true);
    const [newsPage, setNewsPage] = useState(0);
    const [newsRowsPerPage, setNewsRowsPerPage] = useState(10);
    const [newsTotalCount, setNewsTotalCount] = useState(0);
    const [analyzingNews, setAnalyzingNews] = useState(false);
    const [fetchingLiveNews, setFetchingLiveNews] = useState(false);
    const [liveNewsFeedback, setLiveNewsFeedback] = useState(null);
    const [newsSymbolFilter, setNewsSymbolFilter] = useState('');
    const [watchlistSymbols, setWatchlistSymbols] = useState([]);

    // ========== Tab 1: Social Media ==========
    const [socialData, setSocialData] = useState([]);
    const [socialLoading, setSocialLoading] = useState(false);
    const [socialPage, setSocialPage] = useState(0);
    const [socialRowsPerPage, setSocialRowsPerPage] = useState(10);
    const [socialTotalCount, setSocialTotalCount] = useState(0);
    const [socialSymbolFilter, setSocialSymbolFilter] = useState('');
    const [fetchingLiveSocial, setFetchingLiveSocial] = useState(false);
    const [expandedSocialRow, setExpandedSocialRow] = useState(null);
    const [socialPlatformFilter, setSocialPlatformFilter] = useState('twitter');
    const [liveSocialFeedback, setLiveSocialFeedback] = useState(null);

    // ========== Tab 2: Events ==========
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventsPage, setEventsPage] = useState(0);
    const [eventsRowsPerPage, setEventsRowsPerPage] = useState(10);
    const [eventsTotalCount, setEventsTotalCount] = useState(0);
    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [eventForm, setEventForm] = useState({ event_name: '', event_type: 'EARNINGS', event_date: '', symbol_id: '' });
    const [analyzingEvent, setAnalyzingEvent] = useState(false);
    const [eventFeedback, setEventFeedback] = useState(null);
    const [runningPostmortem, setRunningPostmortem] = useState(null);

    // ========== Tab 3: Options Flow ==========
    const [flowData, setFlowData] = useState([]);
    const [flowLoading, setFlowLoading] = useState(false);
    const [flowPage, setFlowPage] = useState(0);
    const [flowRowsPerPage, setFlowRowsPerPage] = useState(10);
    const [flowTotalCount, setFlowTotalCount] = useState(0);
    const [flowSymbolFilter, setFlowSymbolFilter] = useState('');
    const [analyzingFlow, setAnalyzingFlow] = useState(false);
    const [flowFeedback, setFlowFeedback] = useState(null);

    // ========== Tab 4: Macro Indicators ==========
    const [macroData, setMacroData] = useState([]);
    const [macroLoading, setMacroLoading] = useState(false);
    const [macroPage, setMacroPage] = useState(0);
    const [macroRowsPerPage, setMacroRowsPerPage] = useState(10);
    const [macroTotalCount, setMacroTotalCount] = useState(0);
    const [macroIndicator, setMacroIndicator] = useState('');
    const [analyzingMacro, setAnalyzingMacro] = useState(false);
    const [macroFeedback, setMacroFeedback] = useState(null);

    // ========== Permissions ==========
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-aae-sentiment', 'view');
            const add = await hasPermission('tr-aae-sentiment', 'add');
            setCanView(view);
            setCanAdd(add);
        };
        checkPermissions();
    }, []);

    // ========== Fetch Watchlist Symbols ==========
    const fetchWatchlistSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setWatchlistSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch watchlist symbols:', err);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchWatchlistSymbols();
    }, [canView, fetchWatchlistSymbols]);

    // ========== Tab 0: News Sentiment ==========
    const fetchNews = useCallback(async () => {
        if (!canView) return;
        setNewsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: newsPage + 1, per_page: newsRowsPerPage });
            if (newsSymbolFilter) params.append('symbol_id', newsSymbolFilter);
            const response = await axiosInstance.get(`/api/sentiment-data/news?${params.toString()}`);
            if (response.data.success) {
                setNewsItems(response.data.data || []);
                setNewsTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch news sentiment');
        } finally {
            setNewsLoading(false);
        }
    }, [canView, newsPage, newsRowsPerPage, newsSymbolFilter]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchNews();
    }, [fetchNews, canView, activeTab]);

    const handleAnalyzeNews = async () => {
        setAnalyzingNews(true);
        try {
            await axiosInstance.post('/api/sentiment-data/news/analyze');
            fetchNews();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to analyze news');
        } finally {
            setAnalyzingNews(false);
        }
    };

    const handleFetchLiveNews = async () => {
        setFetchingLiveNews(true);
        setLiveNewsFeedback(null);
        try {
            const response = await axiosInstance.post('/api/sentiment-data/news/fetch-live');
            if (response.data.success) {
                const { fetched, stored } = response.data.data || {};
                setLiveNewsFeedback({ type: 'success', message: `Fetched ${fetched} articles, stored ${stored} new records` });
                fetchNews();
            } else {
                setLiveNewsFeedback({ type: 'error', message: response.data.message || 'Failed to fetch live news' });
            }
        } catch (err) {
            setLiveNewsFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to fetch live news' });
        } finally {
            setFetchingLiveNews(false);
        }
    };

    // ========== Tab 1: Social Media ==========
    const fetchSocial = useCallback(async () => {
        if (!canView) return;
        setSocialLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: socialPage + 1, per_page: socialRowsPerPage });
            if (socialSymbolFilter) params.append('symbol_id', socialSymbolFilter);
            const response = await axiosInstance.get(`/api/sentiment-data/social?${params.toString()}`);
            if (response.data.success) {
                setSocialData(response.data.data || []);
                setSocialTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch social data');
        } finally {
            setSocialLoading(false);
        }
    }, [canView, socialPage, socialRowsPerPage, socialSymbolFilter]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchSocial();
    }, [fetchSocial, canView, activeTab]);

    const handleFetchLiveSocial = async () => {
        if (!socialSymbolFilter) {
            setLiveSocialFeedback({ type: 'warning', message: 'Please select a symbol first' });
            return;
        }
        setFetchingLiveSocial(true);
        setLiveSocialFeedback(null);
        try {
            const platformLabels = { twitter: 'Twitter/X', stocktwits: 'StockTwits', reddit: 'Reddit' };
            const response = await axiosInstance.post('/api/sentiment-data/social/fetch-live', { symbol_id: socialSymbolFilter, platform: socialPlatformFilter });
            if (response.data.success) {
                const { symbol, stored, posts_analyzed } = response.data.data || {};
                setLiveSocialFeedback({ type: 'success', message: `Fetched ${posts_analyzed || 0} ${platformLabels[socialPlatformFilter] || 'social'} posts for $${symbol}, stored ${stored} record(s). Click row to view posts.` });
                fetchSocial();
            } else {
                setLiveSocialFeedback({ type: 'error', message: response.data.message || 'Failed to fetch social sentiment' });
            }
        } catch (err) {
            setLiveSocialFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to fetch social sentiment' });
        } finally {
            setFetchingLiveSocial(false);
        }
    };

    // ========== Tab 2: Events ==========
    const fetchEvents = useCallback(async () => {
        if (!canView) return;
        setEventsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: eventsPage + 1, per_page: eventsRowsPerPage });
            const response = await axiosInstance.get(`/api/sentiment-data/events?${params.toString()}`);
            if (response.data.success) {
                setEvents(response.data.data || []);
                setEventsTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch events');
        } finally {
            setEventsLoading(false);
        }
    }, [canView, eventsPage, eventsRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchEvents();
    }, [fetchEvents, canView, activeTab]);

    const handleAnalyzeEvent = async () => {
        if (!eventForm.event_name || !eventForm.event_date) {
            setEventFeedback({ type: 'warning', message: 'Event name and date are required' });
            return;
        }
        setAnalyzingEvent(true);
        setEventFeedback(null);
        try {
            const payload = {
                event_name: eventForm.event_name,
                event_type: eventForm.event_type,
                event_date: eventForm.event_date,
            };
            if (eventForm.symbol_id) payload.symbol_id = eventForm.symbol_id;
            const response = await axiosInstance.post('/api/sentiment-data/events/analyze', payload);
            if (response.data.success) {
                setEventFeedback({ type: 'success', message: `Event "${eventForm.event_name}" analyzed successfully` });
                setEventDialogOpen(false);
                setEventForm({ event_name: '', event_type: 'EARNINGS', event_date: '', symbol_id: '' });
                fetchEvents();
            } else {
                setEventFeedback({ type: 'error', message: response.data.message || 'Failed to analyze event' });
            }
        } catch (err) {
            setEventFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to analyze event' });
        } finally {
            setAnalyzingEvent(false);
        }
    };

    const handleRunPostmortem = async (eventId) => {
        setRunningPostmortem(eventId);
        setEventFeedback(null);
        try {
            const response = await axiosInstance.post(`/api/sentiment-data/events/${eventId}/postmortem`);
            if (response.data.success) {
                setEventFeedback({ type: 'success', message: 'Postmortem analysis completed' });
                fetchEvents();
            } else {
                setEventFeedback({ type: 'error', message: response.data.message || 'Postmortem failed' });
            }
        } catch (err) {
            setEventFeedback({ type: 'error', message: err.response?.data?.message || 'Postmortem failed' });
        } finally {
            setRunningPostmortem(null);
        }
    };

    // ========== Tab 3: Options Flow ==========
    const fetchFlow = useCallback(async () => {
        if (!canView) return;
        setFlowLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: flowPage + 1, per_page: flowRowsPerPage });
            if (flowSymbolFilter) params.append('symbol_id', flowSymbolFilter);
            const response = await axiosInstance.get(`/api/sentiment-data/options-flow?${params.toString()}`);
            if (response.data.success) {
                setFlowData(response.data.data || []);
                setFlowTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch options flow');
        } finally {
            setFlowLoading(false);
        }
    }, [canView, flowPage, flowRowsPerPage, flowSymbolFilter]);

    useEffect(() => {
        if (canView && activeTab === 3) fetchFlow();
    }, [fetchFlow, canView, activeTab]);

    const handleAnalyzeFlow = async () => {
        if (!flowSymbolFilter) {
            setFlowFeedback({ type: 'warning', message: 'Please select a symbol first' });
            return;
        }
        setAnalyzingFlow(true);
        setFlowFeedback(null);
        try {
            const response = await axiosInstance.post(`/api/sentiment-data/options-flow/analyze/${flowSymbolFilter}`);
            if (response.data.success) {
                const records = response.data.data || [];
                setFlowFeedback({ type: 'success', message: `Options flow analyzed, ${records.length} signal(s) detected` });
                fetchFlow();
            } else {
                setFlowFeedback({ type: 'error', message: response.data.message || 'Failed to analyze options flow' });
            }
        } catch (err) {
            setFlowFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to analyze options flow' });
        } finally {
            setAnalyzingFlow(false);
        }
    };

    // ========== Tab 4: Macro Indicators ==========
    const fetchMacro = useCallback(async () => {
        if (!canView) return;
        setMacroLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: macroPage + 1, per_page: macroRowsPerPage });
            const response = await axiosInstance.get(`/api/sentiment-data/macro-indicators?${params.toString()}`);
            if (response.data.success) {
                setMacroData(response.data.data || []);
                setMacroTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch macro indicators');
        } finally {
            setMacroLoading(false);
        }
    }, [canView, macroPage, macroRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 4) fetchMacro();
    }, [fetchMacro, canView, activeTab]);

    const handleAnalyzeMacro = async () => {
        if (!macroIndicator) {
            setMacroFeedback({ type: 'warning', message: 'Please select an indicator first' });
            return;
        }
        setAnalyzingMacro(true);
        setMacroFeedback(null);
        try {
            const response = await axiosInstance.post('/api/sentiment-data/macro/analyze', { indicator_name: macroIndicator });
            if (response.data.success) {
                setMacroFeedback({ type: 'success', message: `${macroIndicator} analyzed successfully` });
                fetchMacro();
            } else {
                setMacroFeedback({ type: 'error', message: response.data.message || 'Failed to analyze indicator' });
            }
        } catch (err) {
            setMacroFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to analyze indicator' });
        } finally {
            setAnalyzingMacro(false);
        }
    };

    // ========== Render Functions ==========
    const renderNewsSentiment = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    News Headlines & Sentiment
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.8rem' }}>Symbol</InputLabel>
                        <Select
                            value={newsSymbolFilter}
                            label="Symbol"
                            onChange={(e) => { setNewsSymbolFilter(e.target.value); setNewsPage(0); }}
                            sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px' }}
                        >
                            <MenuItem value="">
                                <em>All Symbols</em>
                            </MenuItem>
                            {watchlistSymbols.map((sym) => (
                                <MenuItem key={sym.id} value={sym.id} sx={{ fontFamily, fontSize: '0.8rem' }}>
                                    {sym.symbol} — {sym.company_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button startIcon={<RefreshIcon />} onClick={fetchNews} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button startIcon={fetchingLiveNews ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                            variant="contained" onClick={handleFetchLiveNews} disabled={fetchingLiveNews} size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
                            {fetchingLiveNews ? 'Fetching...' : 'Fetch Latest News'}
                        </Button>
                    )}
                    {canAdd && (
                        <Button startIcon={analyzingNews ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            variant="contained" onClick={handleAnalyzeNews} disabled={analyzingNews} size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                            Analyze
                        </Button>
                    )}
                </Box>
                {liveNewsFeedback && (
                    <Alert severity={liveNewsFeedback.type} onClose={() => setLiveNewsFeedback(null)} sx={{ mt: 1, fontFamily }}>
                        {liveNewsFeedback.message}
                    </Alert>
                )}
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {newsLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Headline</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Source</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbols</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Sentiment</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Score</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Published</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }} align="center">Link</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {newsItems.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <NewspaperIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No news items found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : newsItems.map((news) => (
                                        <TableRow key={news.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', maxWidth: 450, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                {news.headline || news.title || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {news.source || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {(news.symbols?.length ? news.symbols : news.related_symbols?.length ? news.related_symbols : news.symbol ? [news.symbol] : []).slice(0, 5).map((sym, i) => (
                                                        <Chip key={i} label={sym} size="small" variant="outlined"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={SentimentScoreLabels[news.sentiment] || news.sentiment || '--'} size="small"
                                                    color={SentimentScoreColors[news.sentiment] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {news.sentiment_score > 0 ? (
                                                        <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                                    ) : news.sentiment_score < 0 ? (
                                                        <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                                    ) : null}
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: news.sentiment_score > 0 ? '#10b981' : news.sentiment_score < 0 ? '#ef4444' : '#64748b', fontSize: '0.8rem' }}>
                                                        {news.sentiment_score != null ? news.sentiment_score.toFixed(3) : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {news.published_at ? new Date(news.published_at).toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {news.url && (
                                                    <IconButton size="small" href={news.url} target="_blank" rel="noopener noreferrer" sx={{ color: '#64748b' }}>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={newsTotalCount} page={newsPage}
                            onPageChange={(e, p) => setNewsPage(p)} rowsPerPage={newsRowsPerPage}
                            onRowsPerPageChange={(e) => { setNewsRowsPerPage(parseInt(e.target.value, 10)); setNewsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderSocialMedia = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Social Media Sentiment
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.8rem' }}>Symbol</InputLabel>
                        <Select
                            value={socialSymbolFilter}
                            label="Symbol"
                            onChange={(e) => { setSocialSymbolFilter(e.target.value); setSocialPage(0); }}
                            sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px' }}
                        >
                            <MenuItem value="">
                                <em>All Symbols</em>
                            </MenuItem>
                            {watchlistSymbols.map((sym) => (
                                <MenuItem key={sym.id} value={sym.id} sx={{ fontFamily, fontSize: '0.8rem' }}>
                                    {sym.symbol} — {sym.company_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.8rem' }}>Platform</InputLabel>
                        <Select
                            value={socialPlatformFilter}
                            label="Platform"
                            onChange={(e) => setSocialPlatformFilter(e.target.value)}
                            sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px' }}
                        >
                            <MenuItem value="twitter" sx={{ fontFamily, fontSize: '0.8rem' }}>Twitter/X</MenuItem>
                            <MenuItem value="stocktwits" sx={{ fontFamily, fontSize: '0.8rem' }}>StockTwits</MenuItem>
                            <MenuItem value="reddit" sx={{ fontFamily, fontSize: '0.8rem' }}>Reddit</MenuItem>
                        </Select>
                    </FormControl>
                    <Button startIcon={<RefreshIcon />} onClick={fetchSocial} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button startIcon={fetchingLiveSocial ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                            variant="contained" onClick={handleFetchLiveSocial} disabled={fetchingLiveSocial} size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
                            {fetchingLiveSocial ? 'Fetching...' : `Fetch ${socialPlatformFilter === 'twitter' ? 'Twitter/X' : socialPlatformFilter === 'stocktwits' ? 'StockTwits' : 'Reddit'} Sentiment`}
                        </Button>
                    )}
                </Box>
            </Box>
            {liveSocialFeedback && (
                <Alert severity={liveSocialFeedback.type} onClose={() => setLiveSocialFeedback(null)} sx={{ mb: 2, fontFamily }}>
                    {liveSocialFeedback.message}
                </Alert>
            )}

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {socialLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Platform</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Mentions</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Sentiment</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Score</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Volume Change</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Engagement</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {socialData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No social media data found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : socialData.map((item) => {
                                        const posts = item.ai_analysis_json?.posts || [];
                                        const hasPosts = posts.length > 0;
                                        const platformLabel = { twitter: 'Twitter/X', stocktwits: 'StockTwits', reddit: 'Reddit' }[item.platform] || item.platform;
                                        const viewLabel = { twitter: 'View on X', stocktwits: 'View on StockTwits', reddit: 'View on Reddit' }[item.platform] || 'View';
                                        return (
                                        <React.Fragment key={item.id}>
                                            <TableRow sx={{ '&:hover': { bgcolor: '#fafbfc' }, cursor: hasPosts ? 'pointer' : 'default' }}
                                                onClick={() => hasPosts && setExpandedSocialRow(expandedSocialRow === item.id ? null : item.id)}>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {item.symbol_name || item.symbol || '--'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={platformLabel} size="small" variant="outlined"
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {item.mention_count != null ? item.mention_count.toLocaleString() : '--'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={SentimentScoreLabels[item.sentiment] || item.sentiment || '--'} size="small"
                                                        color={SentimentScoreColors[item.sentiment] || 'default'}
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, color: item.sentiment_score > 0 ? '#10b981' : item.sentiment_score < 0 ? '#ef4444' : '#64748b', fontSize: '0.82rem' }}>
                                                    {item.sentiment_score != null ? item.sentiment_score.toFixed(3) : '--'}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {item.volume_change_pct > 0 ? (
                                                            <TrendingUpIcon sx={{ fontSize: 16, color: ACCENT }} />
                                                        ) : item.volume_change_pct < 0 ? (
                                                            <TrendingDownIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                        ) : null}
                                                        <Typography sx={{ fontFamily, fontWeight: 600, color: item.volume_change_pct > 50 ? ACCENT : '#64748b', fontSize: '0.8rem' }}>
                                                            {item.volume_change_pct != null ? `${item.volume_change_pct > 0 ? '+' : ''}${item.volume_change_pct.toFixed(1)}%` : '--'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                    {hasPosts ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                                                {posts.length} posts
                                                            </Typography>
                                                            {expandedSocialRow === item.id ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                                        </Box>
                                                    ) : (
                                                        item.engagement_score != null ? item.engagement_score.toLocaleString() : '--'
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                    {item.date ? new Date(item.date).toLocaleDateString() : '--'}
                                                </TableCell>
                                            </TableRow>
                                            {hasPosts && (
                                                <TableRow>
                                                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: expandedSocialRow === item.id ? undefined : 'none' }}>
                                                        <Collapse in={expandedSocialRow === item.id} timeout="auto" unmountOnExit>
                                                            <Box sx={{ py: 1.5, px: 1 }}>
                                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: '#1e293b', mb: 1 }}>
                                                                    {platformLabel} Posts ({posts.length})
                                                                </Typography>
                                                                {posts.map((post, idx) => (
                                                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, p: 1, borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem', color: '#1e293b' }}>
                                                                                {post.username}{post.name ? ` · ${post.name}` : ''}
                                                                                <Typography component="span" sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', ml: 1 }}>
                                                                                    {post.created_at ? new Date(post.created_at).toLocaleString() : ''}
                                                                                </Typography>
                                                                            </Typography>
                                                                            <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#334155', mt: 0.3, wordBreak: 'break-word' }}>
                                                                                {post.text}
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                                                                {item.platform === 'twitter' && (
                                                                                    <>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{post.metrics?.like_count || 0} likes</Typography>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{post.metrics?.retweet_count || 0} retweets</Typography>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{post.metrics?.reply_count || 0} replies</Typography>
                                                                                    </>
                                                                                )}
                                                                                {item.platform === 'stocktwits' && (
                                                                                    <>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{post.metrics?.likes || 0} likes</Typography>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>Sentiment: {post.metrics?.sentiment_tag || 'N/A'}</Typography>
                                                                                    </>
                                                                                )}
                                                                                {item.platform === 'reddit' && (
                                                                                    <>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{post.metrics?.score || 0} score</Typography>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{post.metrics?.comments || 0} comments</Typography>
                                                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{Math.round((post.metrics?.upvote_ratio || 0) * 100)}% upvoted</Typography>
                                                                                    </>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                        {post.url && (
                                                                            <Tooltip title={viewLabel}>
                                                                                <IconButton size="small" component={Link} href={post.url} target="_blank" rel="noopener noreferrer"
                                                                                    sx={{ color: item.platform === 'twitter' ? '#1da1f2' : item.platform === 'reddit' ? '#ff4500' : '#1da1f2', '&:hover': { bgcolor: '#f0f9ff' } }}>
                                                                                    <LaunchIcon sx={{ fontSize: 16 }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                                {item.ai_analysis_json?.summary && (
                                                                    <Box sx={{ mt: 1, p: 1, borderRadius: '8px', bgcolor: '#fffbeb', border: '1px solid #fef3c7' }}>
                                                                        <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#92400e', fontWeight: 500 }}>
                                                                            AI Summary: {item.ai_analysis_json.summary}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );})}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={socialTotalCount} page={socialPage}
                            onPageChange={(e, p) => setSocialPage(p)} rowsPerPage={socialRowsPerPage}
                            onRowsPerPageChange={(e) => { setSocialRowsPerPage(parseInt(e.target.value, 10)); setSocialPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderEvents = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Event Analysis
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button startIcon={<RefreshIcon />} onClick={fetchEvents} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setEventDialogOpen(true)} size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
                            Analyze Event
                        </Button>
                    )}
                </Box>
            </Box>
            {eventFeedback && (
                <Alert severity={eventFeedback.type} onClose={() => setEventFeedback(null)} sx={{ mb: 2, fontFamily }}>
                    {eventFeedback.message}
                </Alert>
            )}

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {eventsLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Event</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbols</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Impact Level</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Vol Impact</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Expected Date</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Status</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {events.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No events found. Click "Analyze Event" to add one.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : events.map((event) => (
                                        <TableRow key={event.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Tooltip title={event.ai_preview || event.name || event.description || ''}>
                                                    <span>{event.name || event.description || '--'}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={event.event_type || '--'} size="small" variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {(event.affected_symbols || []).slice(0, 3).map((sym, i) => (
                                                        <Chip key={i} label={sym} size="small" variant="outlined"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={EventImpactLevelLabels[event.impact_level] || event.impact_level || '--'} size="small"
                                                    color={EventImpactLevelColors[event.impact_level] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {event.expected_vol_impact != null ? `${event.expected_vol_impact > 0 ? '+' : ''}${(event.expected_vol_impact * 100).toFixed(1)}%` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {event.expected_date ? new Date(event.expected_date).toLocaleDateString() : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={event.status || 'Upcoming'} size="small"
                                                    color={event.status === 'COMPLETED' ? 'default' : event.status === 'ACTIVE' ? 'success' : 'info'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                {event.status !== 'COMPLETED' && (
                                                    <Tooltip title="Run Postmortem Analysis">
                                                        <IconButton size="small" onClick={() => handleRunPostmortem(event.id)}
                                                            disabled={runningPostmortem === event.id}
                                                            sx={{ color: '#f59e0b' }}>
                                                            {runningPostmortem === event.id ? <CircularProgress size={16} /> : <AssessmentIcon sx={{ fontSize: 18 }} />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {event.status === 'COMPLETED' && (
                                                    <Tooltip title={event.ai_postmortem || 'Postmortem completed'}>
                                                        <Chip label="Done" size="small" color="success" sx={{ fontFamily, fontSize: '0.65rem', height: 20 }} />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={eventsTotalCount} page={eventsPage}
                            onPageChange={(e, p) => setEventsPage(p)} rowsPerPage={eventsRowsPerPage}
                            onRowsPerPageChange={(e) => { setEventsRowsPerPage(parseInt(e.target.value, 10)); setEventsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>

            {/* Analyze Event Dialog */}
            <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Analyze Market Event</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    {eventFeedback && eventDialogOpen && (
                        <Alert severity={eventFeedback.type} onClose={() => setEventFeedback(null)} sx={{ fontFamily }}>
                            {eventFeedback.message}
                        </Alert>
                    )}
                    <TextField label="Event Name" fullWidth size="small" value={eventForm.event_name}
                        onChange={(e) => setEventForm({ ...eventForm, event_name: e.target.value })}
                        placeholder="e.g. AAPL Q1 2026 Earnings Report"
                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                        InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Event Type</InputLabel>
                        <Select value={eventForm.event_type} label="Event Type"
                            onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
                            sx={{ fontFamily, fontSize: '0.85rem' }}>
                            <MenuItem value="EARNINGS" sx={{ fontFamily, fontSize: '0.85rem' }}>Earnings Report</MenuItem>
                            <MenuItem value="FDA_APPROVAL" sx={{ fontFamily, fontSize: '0.85rem' }}>FDA Approval</MenuItem>
                            <MenuItem value="MERGER_ACQUISITION" sx={{ fontFamily, fontSize: '0.85rem' }}>Merger & Acquisition</MenuItem>
                            <MenuItem value="IPO" sx={{ fontFamily, fontSize: '0.85rem' }}>IPO</MenuItem>
                            <MenuItem value="DIVIDEND" sx={{ fontFamily, fontSize: '0.85rem' }}>Dividend</MenuItem>
                            <MenuItem value="STOCK_SPLIT" sx={{ fontFamily, fontSize: '0.85rem' }}>Stock Split</MenuItem>
                            <MenuItem value="REGULATORY" sx={{ fontFamily, fontSize: '0.85rem' }}>Regulatory</MenuItem>
                            <MenuItem value="ECONOMIC" sx={{ fontFamily, fontSize: '0.85rem' }}>Economic Data</MenuItem>
                            <MenuItem value="GEOPOLITICAL" sx={{ fontFamily, fontSize: '0.85rem' }}>Geopolitical</MenuItem>
                            <MenuItem value="OTHER" sx={{ fontFamily, fontSize: '0.85rem' }}>Other</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Event Date" type="date" fullWidth size="small" value={eventForm.event_date}
                        onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                        InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Symbol (Optional)</InputLabel>
                        <Select value={eventForm.symbol_id} label="Symbol (Optional)"
                            onChange={(e) => setEventForm({ ...eventForm, symbol_id: e.target.value })}
                            sx={{ fontFamily, fontSize: '0.85rem' }}>
                            <MenuItem value="" sx={{ fontFamily, fontSize: '0.85rem' }}>
                                <em>No specific symbol</em>
                            </MenuItem>
                            {watchlistSymbols.map((sym) => (
                                <MenuItem key={sym.id} value={sym.id} sx={{ fontFamily, fontSize: '0.85rem' }}>
                                    {sym.symbol} — {sym.company_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEventDialogOpen(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleAnalyzeEvent} disabled={analyzingEvent}
                        startIcon={analyzingEvent ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
                        {analyzingEvent ? 'Analyzing...' : 'Analyze with AI'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );

    const renderOptionsFlow = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Options Flow & Unusual Activity
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.8rem' }}>Symbol</InputLabel>
                        <Select
                            value={flowSymbolFilter}
                            label="Symbol"
                            onChange={(e) => { setFlowSymbolFilter(e.target.value); setFlowPage(0); }}
                            sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px' }}
                        >
                            <MenuItem value="">
                                <em>All Symbols</em>
                            </MenuItem>
                            {watchlistSymbols.map((sym) => (
                                <MenuItem key={sym.id} value={sym.id} sx={{ fontFamily, fontSize: '0.8rem' }}>
                                    {sym.symbol} — {sym.company_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button startIcon={<RefreshIcon />} onClick={fetchFlow} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button startIcon={analyzingFlow ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            variant="contained" onClick={handleAnalyzeFlow} disabled={analyzingFlow} size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
                            {analyzingFlow ? 'Analyzing...' : 'Analyze Options Flow'}
                        </Button>
                    )}
                </Box>
            </Box>
            {flowFeedback && (
                <Alert severity={flowFeedback.type} onClose={() => setFlowFeedback(null)} sx={{ mb: 2, fontFamily }}>
                    {flowFeedback.message}
                </Alert>
            )}

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {flowLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Signal Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Side</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Volume</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Premium</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Vol/OI Ratio</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Expiry</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Time</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {flowData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No unusual options flow detected. Select a symbol and click "Analyze Options Flow".</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : flowData.map((flow) => (
                                        <TableRow key={flow.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {flow.symbol_name || flow.symbol || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={FlowSignalTypeLabels[flow.signal_type] || flow.signal_type || '--'} size="small"
                                                    color={FlowSignalTypeColors[flow.signal_type] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={flow.side || '--'} size="small"
                                                    color={flow.side === 'CALL' ? 'success' : flow.side === 'PUT' ? 'error' : 'default'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {flow.volume != null ? flow.volume.toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: ACCENT, fontSize: '0.82rem' }}>
                                                {flow.premium != null ? `$${(flow.premium / 1000).toFixed(1)}K` : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(flow.vol_oi_ratio != null ? flow.vol_oi_ratio * 20 : 0, 100)}
                                                        sx={{
                                                            width: 40, height: 4, borderRadius: 2, bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: flow.vol_oi_ratio > 3 ? '#ef4444' : flow.vol_oi_ratio > 1.5 ? ACCENT : '#10b981',
                                                                borderRadius: 2,
                                                            },
                                                        }}
                                                    />
                                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                        {flow.vol_oi_ratio != null ? flow.vol_oi_ratio.toFixed(2) : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {flow.expiry ? new Date(flow.expiry).toLocaleDateString() : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {flow.timestamp ? new Date(flow.timestamp).toLocaleTimeString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={flowTotalCount} page={flowPage}
                            onPageChange={(e, p) => setFlowPage(p)} rowsPerPage={flowRowsPerPage}
                            onRowsPerPageChange={(e) => { setFlowRowsPerPage(parseInt(e.target.value, 10)); setFlowPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const macroIndicatorOptions = [
        'GDP Growth Rate', 'CPI (Consumer Price Index)', 'Core CPI', 'PPI (Producer Price Index)',
        'Non-Farm Payrolls', 'Unemployment Rate', 'Federal Funds Rate', 'ISM Manufacturing PMI',
        'ISM Services PMI', 'Retail Sales', 'Consumer Confidence Index', 'Housing Starts',
        '10-Year Treasury Yield', 'VIX (Volatility Index)', 'US Dollar Index (DXY)',
        'Crude Oil (WTI)', 'Gold Price', 'Initial Jobless Claims', 'PCE Price Index',
    ];

    const renderMacroIndicators = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Macro Indicators
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.8rem' }}>Indicator</InputLabel>
                        <Select
                            value={macroIndicator}
                            label="Indicator"
                            onChange={(e) => setMacroIndicator(e.target.value)}
                            sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px' }}
                        >
                            <MenuItem value="">
                                <em>Select Indicator</em>
                            </MenuItem>
                            {macroIndicatorOptions.map((ind) => (
                                <MenuItem key={ind} value={ind} sx={{ fontFamily, fontSize: '0.8rem' }}>
                                    {ind}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button startIcon={<RefreshIcon />} onClick={fetchMacro} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button startIcon={analyzingMacro ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            variant="contained" onClick={handleAnalyzeMacro} disabled={analyzingMacro} size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
                            {analyzingMacro ? 'Analyzing...' : 'Analyze Indicator'}
                        </Button>
                    )}
                </Box>
            </Box>
            {macroFeedback && (
                <Alert severity={macroFeedback.type} onClose={() => setMacroFeedback(null)} sx={{ mb: 2, fontFamily }}>
                    {macroFeedback.message}
                </Alert>
            )}

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {macroLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Indicator</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Signal</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Current Value</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Previous</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Change</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Impact on Vol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Last Updated</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {macroData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No macro indicator data found. Select an indicator and click "Analyze Indicator".</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : macroData.map((macro) => (
                                        <TableRow key={macro.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {macro.indicator_name || macro.indicator || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={MacroIndicatorSignalLabels[macro.signal] || macro.signal || '--'} size="small"
                                                    color={MacroIndicatorSignalColors[macro.signal] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {macro.current_value != null ? macro.current_value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {macro.previous_value != null ? macro.previous_value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {macro.change_pct > 0 ? (
                                                        <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                                    ) : macro.change_pct < 0 ? (
                                                        <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                                    ) : null}
                                                    <Typography sx={{
                                                        fontFamily, fontWeight: 600, fontSize: '0.8rem',
                                                        color: macro.change_pct > 0 ? '#10b981' : macro.change_pct < 0 ? '#ef4444' : '#64748b',
                                                    }}>
                                                        {macro.change_pct != null ? `${macro.change_pct > 0 ? '+' : ''}${macro.change_pct.toFixed(2)}%` : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {macro.vol_impact || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {macro.updated_at ? new Date(macro.updated_at).toLocaleString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={macroTotalCount} page={macroPage}
                            onPageChange={(e, p) => setMacroPage(p)} rowsPerPage={macroRowsPerPage}
                            onRowsPerPageChange={(e) => { setMacroRowsPerPage(parseInt(e.target.value, 10)); setMacroPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ========== Permission Guard ==========
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view Sentiment & Alternative Data.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <NewspaperIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Sentiment & Alternative Data
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            News sentiment, social media, events, options flow, and macro indicators
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
                    <Tab label="News Sentiment" icon={<NewspaperIcon />} iconPosition="start" />
                    <Tab label="Social Media" icon={<ForumIcon />} iconPosition="start" />
                    <Tab label="Events" icon={<EventIcon />} iconPosition="start" />
                    <Tab label="Options Flow" icon={<WavesIcon />} iconPosition="start" />
                    <Tab label="Macro Indicators" icon={<AccountBalanceIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderNewsSentiment()}
            {activeTab === 1 && renderSocialMedia()}
            {activeTab === 2 && renderEvents()}
            {activeTab === 3 && renderOptionsFlow()}
            {activeTab === 4 && renderMacroIndicators()}
        </Box>
    );
};

export default SentimentAlternativeDataPage;
